/* ==========================================================================
   ReLEAF — Find a Provider locator
   1. Geocodes a US ZIP code via Zippopotam.us (free, keyless, CORS-enabled).
   2. Filters the merchant's provider list to a radius using the Haversine
      formula, sorts by distance, and renders both a results list and pins on
      a self-hosted Leaflet map.
   3. Bound to shopify:section:load so the Theme Editor's re-render doesn't
      leave the map dead.

   ZIPPOPOTAM DEPENDENCY — READ BEFORE CHANGING
   ---------------------------------------------------------------------------
   Zippopotam.us has no official uptime SLA. It has been stable and free for
   years and needs no API key, which is why it's used here rather than a paid
   geocoder. If it becomes unreliable in production, replace only the body of
   `geocodeZip()` below with a call to a keyed provider (Google Maps Geocoding
   API, Mapbox). Everything downstream (distance filtering, map, results)
   consumes a plain {lat, lng} object and does not need to change.
   ========================================================================== */
window.ReLEAFFAP = (function () {
  "use strict";

  var EARTH_RADIUS_MI = 3958.8;

  function toRad(deg) { return (deg * Math.PI) / 180; }

  function haversineMiles(lat1, lon1, lat2, lon2) {
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_MI * c;
  }

  /* Single swap point if Zippopotam is ever replaced. Must resolve to
     {lat: Number, lng: Number} or reject. */
  function geocodeZip(zip) {
    return fetch("https://api.zippopotam.us/us/" + encodeURIComponent(zip))
      .then(function (res) {
        if (!res.ok) throw new Error("not-found");
        return res.json();
      })
      .then(function (data) {
        var place = data && data.places && data.places[0];
        if (!place) throw new Error("not-found");
        return {
          lat: parseFloat(place.latitude),
          lng: parseFloat(place.longitude)
        };
      });
  }

  function cleanPhoneHref(phone) {
    return "tel:" + String(phone || "").replace(/[^\d+]/g, "");
  }

  function directionsUrl(lat, lng) {
    return "https://www.google.com/maps/dir/?api=1&destination=" + lat + "," + lng;
  }

  function initLocator(root) {
    var scope = root || document;
    var el = scope.querySelector("[data-rl-fap-root]");
    if (!el || el.dataset.rlBound) return;
    el.dataset.rlBound = "1";

    var dataEl = el.querySelector("[data-rl-fap-providers]");
    var providers = [];
    try {
      providers = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      providers = [];
    }
    // Only providers with real, filled-in coordinates can ever appear on the
    // map. A blank Shopify "number" setting comes through as JSON null, and
    // `isFinite(null)` is true (null coerces to 0) — so isFinite alone would
    // silently plot an unconfigured provider in the Gulf of Guinea. typeof
    // must be checked first.
    providers = providers.filter(function (p) {
      return typeof p.lat === "number" && typeof p.lng === "number" &&
        isFinite(p.lat) && isFinite(p.lng);
    });
    // Stable ids so a result card and its map pin can find each other
    providers.forEach(function (p, i) { p._id = i; });

    var form = el.querySelector("[data-rl-fap-form]");
    var input = el.querySelector("[data-rl-fap-input]");
    var submitBtn = el.querySelector("[data-rl-fap-submit]");
    var resetBtn = el.querySelector("[data-rl-fap-reset]");
    var errorMsg = el.querySelector("[data-rl-fap-error]");
    var noResultsMsg = el.querySelector("[data-rl-fap-noresults]");
    var resultsWrap = el.querySelector("[data-rl-fap-results]");
    var countEl = el.querySelector("[data-rl-fap-count]");
    var mapEl = el.querySelector("[data-rl-fap-map]");
    var panel = el.querySelector("[data-rl-fap-panel]");
    var frameEl = el.querySelector(".rl-fap-mapwrap");
    var reduceMotion = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    var radiusMiles = parseFloat(el.getAttribute("data-radius")) || 100;
    var noResultsText = el.getAttribute("data-no-results-text") ||
      "No available provider within " + radiusMiles + " miles of your location.";
    var errorText = el.getAttribute("data-error-text") ||
      "We couldn\u2019t verify that ZIP code. Please check it and try again.";
    var distanceUnit = el.getAttribute("data-distance-unit") || "mi";
    var countTemplate = el.getAttribute("data-count-text") || "{count} providers found";

    /* ---------------------------------------------------------- the map */
    if (typeof L === "undefined" || !mapEl) return; // Leaflet failed to load; the form still degrades to a plain list below

    var defaultCenter = [
      parseFloat(el.getAttribute("data-center-lat")) || 39.8,
      parseFloat(el.getAttribute("data-center-lng")) || -98.6
    ];
    var defaultZoom = parseInt(el.getAttribute("data-default-zoom"), 10) || 4;

    // Zoom buttons sit top-right: the results panel covers the map's left edge.
    var map = L.map(mapEl, { scrollWheelZoom: false, zoomControl: false })
      .setView(defaultCenter, defaultZoom);
    L.control.zoom({ position: "topright" }).addTo(map);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: "\u00A9 OpenStreetMap contributors"
    }).addTo(map);

    /* Leaflet measures its container's size exactly once, at the instant
       .map() runs. If the section is still fading/sliding into view at that
       moment (Dawn's scroll-reveal animation, present on most sections by
       default) the container's real box isn't final yet, and Leaflet never
       re-measures on its own — the map renders at whatever size it guessed
       and stays that way, which is what produces an oversized or malformed
       map once the section settles. Telling it to remeasure at each of these
       points covers every way that can happen. */
    function fixMapSize() { map.invalidateSize(); }

    setTimeout(fixMapSize, 0);
    window.addEventListener("load", fixMapSize);

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fixMapSize, 150);
    });

    if ("IntersectionObserver" in window) {
      var seen = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !seen) {
            seen = true;
            fixMapSize();
            // A second pass a beat later catches a reveal transition that's
            // still mid-animation on first intersection.
            setTimeout(fixMapSize, 350);
          }
        });
      }, { threshold: 0.1 });
      io.observe(mapEl);
    }

    var markerLayer = L.layerGroup().addTo(map);

    function popupHtml(p) {
      var lines = [];
      if (p.address) lines.push('<span class="rl-fap-popup__line">' + p.address + "</span>");
      if (p.phone) {
        lines.push('<span class="rl-fap-popup__line"><a href="' + cleanPhoneHref(p.phone) + '">' + p.phone + "</a></span>");
      }
      if (p.website) {
        lines.push('<span class="rl-fap-popup__line"><a href="' + p.website + '" target="_blank" rel="noopener">Visit website</a></span>');
      }
      return '<span class="rl-fap-popup__name">' + p.name + "</span>" + lines.join("");
    }

    var markersById = {}, rowsById = {};

    /* Width of the map the results panel is currently covering (0 when the
       panel is closed or, on phones, stacked above the map instead). */
    function panelInset() {
      if (!panel || !panel.classList.contains("is-open")) return 0;
      if (window.getComputedStyle(panel).position !== "absolute") return 0;
      return panel.offsetWidth + 24;
    }

    /* Centre a point in the part of the map the panel isn't covering. */
    function viewOn(latlng, zoom) {
      var pt = map.project(latlng, zoom).subtract([panelInset() / 2, 0]);
      map.setView(map.unproject(pt, zoom), zoom);
    }

    function plot(list) {
      markerLayer.clearLayers();
      markersById = {};
      var inset = panelInset(), pts = [];
      list.forEach(function (p) {
        var m = L.marker([p.lat, p.lng]).bindPopup(popupHtml(p), {
          autoPanPaddingTopLeft: L.point(inset + 16, 16),
          autoPanPaddingBottomRight: L.point(16, 16)
        });
        m.on("click", function () { select(p._id, true); });
        markerLayer.addLayer(m);
        markersById[p._id] = m;
        pts.push([p.lat, p.lng]);
      });
      if (pts.length === 1) {
        viewOn(L.latLng(pts[0]), 11);
      } else if (pts.length > 1) {
        // Fit the pins to the visible part of the map, not the whole box,
        // or the panel would sit on top of the ones nearest the left edge.
        map.fitBounds(pts, {
          paddingTopLeft: [inset + 28, 28],
          paddingBottomRight: [28, 28]
        });
      } else {
        map.setView(defaultCenter, defaultZoom);
      }
    }

    /* Highlight a result and its pin. A click on a card flies to the pin;
       a click on a pin scrolls its card into view inside the panel. */
    function select(id, fromMarker) {
      Object.keys(rowsById).forEach(function (k) {
        rowsById[k].classList.toggle("is-selected", String(k) === String(id));
      });
      var row = rowsById[id], m = markersById[id];
      if (row && fromMarker) {
        var top = Math.max(0, row.offsetTop - 8);
        if (resultsWrap.scrollTo) {
          resultsWrap.scrollTo({ top: top, behavior: reduceMotion ? "auto" : "smooth" });
        } else {
          resultsWrap.scrollTop = top;
        }
      }
      if (m && !fromMarker) {
        viewOn(m.getLatLng(), Math.max(map.getZoom(), 10));
        m.openPopup();
      }
    }

    // Initial state: every configured location, per the brief
    plot(providers);

    /* ------------------------------------------------------ results list */
    function clearMessages() {
      errorMsg.classList.remove("is-visible");
      noResultsMsg.classList.remove("is-visible");
    }

    function renderResults(list) {
      resultsWrap.innerHTML = "";
      rowsById = {};
      if (!list.length) return;

      list.forEach(function (p) {
        var row = document.createElement("div");
        row.className = "rl-fap-result";
        rowsById[p._id] = row;

        var head = document.createElement("div");
        head.style.cssText = "display:flex;flex:1 1 100%;justify-content:space-between;align-items:baseline;gap:.8rem;";
        // A real button, so keyboard users can focus a result and see its pin
        var name = document.createElement("button");
        name.type = "button";
        name.className = "rl-fap-result__name";
        name.textContent = p.name;
        name.addEventListener("click", function () { select(p._id, false); });
        var dist = document.createElement("span");
        dist.className = "rl-fap-result__distance";
        dist.textContent = p.distance.toFixed(1) + " " + distanceUnit;
        head.appendChild(name);
        head.appendChild(dist);
        row.appendChild(head);

        var meta = document.createElement("div");
        meta.className = "rl-fap-result__row";
        if (p.address) {
          var addr = document.createElement("span");
          addr.textContent = p.address;
          meta.appendChild(addr);
        }
        if (p.phone) {
          var tel = document.createElement("a");
          tel.href = cleanPhoneHref(p.phone);
          tel.textContent = p.phone;
          meta.appendChild(tel);
        }
        if (p.website) {
          var site = document.createElement("a");
          site.href = p.website;
          site.target = "_blank";
          site.rel = "noopener";
          site.textContent = "Website";
          meta.appendChild(site);
        }
        var dir = document.createElement("a");
        dir.href = directionsUrl(p.lat, p.lng);
        dir.target = "_blank";
        dir.rel = "noopener";
        dir.textContent = "Directions";
        meta.appendChild(dir);
        row.appendChild(meta);

        // Anywhere on the card (except its own links) does the same as the name
        row.addEventListener("click", function (e) {
          if (e.target.closest("a") || e.target.closest("button")) return;
          select(p._id, false);
        });

        resultsWrap.appendChild(row);
      });
    }

    function openPanel() {
      if (!panel) return;
      panel.classList.add("is-open");
      resultsWrap.scrollTop = 0;
    }
    function closePanel() {
      if (panel) panel.classList.remove("is-open");
      rowsById = {};
    }
    /* Bring the map and its results panel into view. `nearest` moves the page
       only as far as needed, so a visitor who can already see them doesn't
       get yanked around, and the search box stays on screen. */
    function revealFrame() {
      if (frameEl && frameEl.scrollIntoView) {
        frameEl.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "nearest" });
      }
    }

    function runSearch(origin) {
      var withDistance = providers.map(function (p) {
        return Object.assign({}, p, {
          distance: haversineMiles(origin.lat, origin.lng, p.lat, p.lng)
        });
      });
      var inRange = withDistance
        .filter(function (p) { return p.distance <= radiusMiles; })
        .sort(function (a, b) { return a.distance - b.distance; });

      clearMessages();
      if (!inRange.length) {
        noResultsMsg.textContent = noResultsText;
        noResultsMsg.classList.add("is-visible");
        resultsWrap.innerHTML = "";
        countEl.textContent = "";
        closePanel();
        plot(providers); // fall back to showing everything, since none matched
      } else {
        renderResults(inRange);
        countEl.textContent = countTemplate.replace("{count}", inRange.length);
        openPanel();       // must open before plot(), which fits pins around it
        plot(inRange);
      }
      resetBtn.classList.add("is-visible");
      // Last, once every layout change above has landed, so the scroll is
      // computed against the page as it will actually be.
      if (inRange.length) revealFrame();
    }

    function setBusy(isBusy) {
      submitBtn.disabled = isBusy;
      submitBtn.querySelector("[data-rl-fap-submit-label]").textContent =
        isBusy ? (el.getAttribute("data-searching-text") || "Searching\u2026")
               : (el.getAttribute("data-search-label") || "Search");
    }

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      clearMessages();
      var zip = (input.value || "").trim();

      if (!/^\d{5}$/.test(zip)) {
        errorMsg.textContent = el.getAttribute("data-format-error-text") ||
          "Enter a valid 5-digit ZIP code.";
        errorMsg.classList.add("is-visible");
        return;
      }

      setBusy(true);
      geocodeZip(zip)
        .then(function (origin) {
          setBusy(false);
          runSearch(origin);
        })
        .catch(function () {
          setBusy(false);
          errorMsg.textContent = errorText;
          errorMsg.classList.add("is-visible");
          resultsWrap.innerHTML = "";
          countEl.textContent = "";
          closePanel();
        });
    });

    resetBtn.addEventListener("click", function () {
      clearMessages();
      resultsWrap.innerHTML = "";
      countEl.textContent = "";
      input.value = "";
      resetBtn.classList.remove("is-visible");
      closePanel();
      plot(providers);
    });
  }

  function initAll(root) { initLocator(root); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });

  return { initAll: initAll, haversineMiles: haversineMiles };
})();
